import { useEffect } from "react";
import { autorun, runInAction } from "mobx";

/**
 *
 * @param {object} CommunicationStore vom Sandboxviewer
 * @param {string} attributeId welches im Klimaslider angezeigt werden soll
 * @returns {string} attributeId
 */
const PossibilityUpdater = ({ CommunicationStore, attributeId }) => {
  useEffect(() => {
    // Use autorun instead of observer() so MobX drives the reactive logic
    // directly, without ever calling React's forceUpdate on this component.
    // This eliminates the "update PossibilityUpdater while rendering
    // URLParameterController" warning: with observer() MobX would call
    // forceUpdate(PossibilityUpdater) synchronously inside endBatch() while
    // React 18's concurrent renderer still has URLParameterController's
    // fiber in-flight, triggering the conflict check. With autorun, MobX
    // runs the callback directly — no React forceUpdate on this component.
    const dispose = autorun(() => {
      const getLayerPossibilities = CommunicationStore.getMessagesToID({
        to: "getLayerPossibilities",
      })?.message;

      const filterAccess =
        CommunicationStore.getMessagesToID({
          to: "filterAccess",
        })?.message(attributeId, () => true) || {};
      const { items = [], visible } = filterAccess;

      if (!getLayerPossibilities) return;

      const level = CommunicationStore.getMessagesToID({
        to: "filterLevel",
      })?.message;
      if (!level) return;

      const choicesLabel = items.map(({ label }) => label);
      const excluded = { [attributeId]: choicesLabel };
      const cloneLevel = [].concat(level);
      cloneLevel.splice(cloneLevel.indexOf(attributeId) + 1).forEach((l) => {
        excluded[l] = [];
      });
      const possibilities = getLayerPossibilities(excluded);

      const choicesWithMeta = items.map((choice) => {
        const { label } = choice;
        const possi = possibilities.filter((p) => {
          const poss = p.possibilities[attributeId] ?? [];
          if (poss.length > 1) {
            // This means multiple layersConstraint entries share the same label for this attributeId, which causes ambiguity in the slider:
            // we won't know which one to enable when that label is selected.
            // Disable all affected ticks by returning an empty array.
            return false;
          }
          return poss[0] === label;
        });
        runInAction(() => {
          choice.possibilities = possi;
          choice.nopossibilities = possi.length;
        });
        return choice;
      });

      CommunicationStore.addMessage({
        to: "choicesWithMeta",
        message: visible ? choicesWithMeta : [],
      });
    });

    return dispose;
  }, [CommunicationStore, attributeId]);

  return <div style={{ display: "none" }} />;
};

export default PossibilityUpdater;
